'use client';

import { motion } from 'framer-motion';
import { useId } from 'react';

export type ToggleOption<T extends string | boolean> = {
  value: T;
  label: string;
};

type AnimatedToggleProps<T extends string | boolean> = {
  options: ToggleOption<T>[];
  value: T;
  onChange: (value: T) => void;
  // Fixed pixel width per button. Omit for a full-width, equal-flex layout.
  buttonWidth?: number;
  trackClassName?: string;
  pillClassName?: string;
  buttonClassName?: string;
  activeTextClassName?: string;
  inactiveTextClassName?: string;
};

// Segmented control with a sliding pill behind the active option. Shared by the
// search page, the vehicle landing pages (condition filter) and the create
// listing shipping toggle. Styling defaults to the condition-filter look;
// callers pass class overrides for other variants.
export default function AnimatedToggle<T extends string | boolean>({
  options,
  value,
  onChange,
  buttonWidth,
  trackClassName = 'h-[4.5rem] rounded-[1.5rem] bg-[#F1F1F1]',
  pillClassName = 'rounded-[1.3rem] bg-[#444]',
  buttonClassName = 'text-[1.6rem] font-medium',
  activeTextClassName = 'text-white',
  inactiveTextClassName = 'text-gray-700 hover:text-gray-900',
}: AnimatedToggleProps<T>) {
  // Unique per instance so two toggles never share a layout-animation group.
  const layoutId = useId();
  const isFixed = typeof buttonWidth === 'number';
  const activeIndex = options.findIndex((option) => option.value === value);

  return (
    <div
      className={`relative flex items-center ${trackClassName}`}
      style={{ width: isFixed ? buttonWidth! * options.length : '100%' }}
    >
      <motion.div
        className={`absolute ${pillClassName}`}
        layoutId={layoutId}
        transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
        animate={{
          height: 'calc(100% - 0.8rem)',
          width: isFixed
            ? buttonWidth! - 8
            : `calc(${100 / options.length}% - 8px)`,
          left: isFixed
            ? `${activeIndex * buttonWidth! + 4}px`
            : `calc(${activeIndex * (100 / options.length)}% + 4px)`,
        }}
      />
      {options.map((option, i) => (
        <button
          type="button"
          key={i}
          onClick={() => onChange(option.value)}
          className={`relative z-10 flex h-full items-center justify-center rounded-full transition-colors ${buttonClassName} ${
            value === option.value
              ? activeTextClassName
              : inactiveTextClassName
          }`}
          style={{
            width: isFixed ? `${buttonWidth}px` : undefined,
            flex: isFixed ? undefined : 1,
          }}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}
